import {
    Component, computed, effect, inject, input, OnInit, output, signal, untracked, ChangeDetectionStrategy,
} from '@angular/core';
import { fb, translate } from '../../../../../modules/shared/shared.global-service';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SaveButtonComponent } from '../../../../kit/button/save-button.component';
import { featureOn } from '../../../../../feature-flag';
import { TranslatePipe } from '@ngx-translate/core';
import {
    SelectMedicationComponent,
} from '../../../../../modules/content/components/common/select-medication/select-medication.component';
import { FolderEPrescriptionFormStore } from './folder-e-prescription-form-store.service';
import { formatPerson } from '../../../../../modules/shared/pipes/format-person.pipe';
import {
    FolderEPrescriptionNotificationModalComponent,
} from '../folder-e-prescription-notification-modal/folder-e-prescription-notification-modal.component';
import { EPrescriptionObject } from '@libs/types/src/services/object-type/sukl/e-prescription.object';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { AddressDetailObject } from '@it2go/types';
import { mapAddress } from '../../../../../modules/shared/helpers/map-address.helper';
import {
    EPrescriptionMedicationUpsertInput,
} from '@libs/types/src/services/input-type/sukl/e-prescription-medication.upsert-input';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    EPrescriptionNotificationEnum,
    ePrescriptionNotificationEnumOptions,
} from '@libs/types/src/services/enum/sukl/e-prescription.notification-enum';
import { RadioInputComponent } from '../../../../kit/input/radio-input/radio-input.component';
import { folderFrag } from '../../../../../services/types/folder.types';
import {
    ServiceDoctorSelectInputComponent,
} from '../../../element/service-doctor-select-input/service-doctor-select-input.component';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { getCurrentInsurance } from '../../../../../utils/insurance.utils';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { CheckInputComponent } from '../../../../kit/input/check-input/check-input.component';
import { AddressInputComponent } from '../../../../kit/input/address-input/address-input.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../element/insurance-company-select-input/insurance-company-select-input.component';
import {
    EPrescriptionMedicationRecordObject,
} from '@libs/types/src/services/object-type/sukl/e-prescription-medication-record.object';
import { FolderEPrescriptionMedicationService } from '../folder-e-prescription-medication.service';
import {
    SERVICE_SETTINGS_EPRESCRIPTIONS_TYPE_DEFAULT,
    SERVICE_SETTINGS_EPRESCRIPTIONS_TYPE_KEY,
    ServiceSettingsEprescriptionType,
} from '@libs/types/src/services/types/service-settings-eprescription.type';
import { PhoneInputComponent } from '../../../../kit/input/phone-input/phone-input.component';
import { FieldErrorFactory } from '../../../../../modules/shared/service/field-error.service';
import {
    EPrescriptionItemTypeEnum,
    ePrescriptionItemTypeEnumOptions,
} from '@libs/types/src/services/enum/sukl/e-prescription.item-type-enum';
import {
    EPrescriptionIngredientUnitEnum,
    ePrescriptionIngredientUnitEnumOptions,
    getEPrescriptionIngredientUnitForForm,
} from '@libs/types/src/services/enum/sukl/e-prescription-ingredient-unit.enum';
import {
    EPrescriptionMagistraliterIngredientUpsertInput,
} from '@libs/types/src/services/input-type/sukl/e-prescription-magistraliter-ingredient.upsert-input';
import {
    EPrescriptionMagistraliterUpsertInput,
} from '@libs/types/src/services/input-type/sukl/e-prescription-magistraliter.upsert-input';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { TextAreaInputComponent } from '../../../../kit/input/text-area-input/text-area-input.component';
import {
    E_PRESCRIPTION_CANNABIS_SUBSTANCES,
    EPrescriptionMagistraliterIngredientTypeEnum,
    ePrescriptionMagistraliterIngredientTypeEnumOptions,
    isEPrescriptionCannabisSubstanceCode,
} from '@libs/types/src/services/enum/sukl/e-prescription-magistraliter-ingredient-type.enum';
import { MedicationGridItemObject } from '@libs/types/src/services/object-type/medication/medication.object';
import { WarnFloatMessageComponent } from '../../../../kit/float-message/warn-float-message.component';
import {
    FolderEPrescriptionMagistraliterTemplatesDialogComponent,
} from '../folder-e-prescription-magistraliter-templates-dialog/folder-e-prescription-magistraliter-templates-dialog.component';
import {
    EPrescriptionMagistraliterTemplateObject,
} from '@libs/types/src/services/object-type/sukl/e-prescription-magistraliter-template.object';
import {
    InputTextAutocompleteComponent,
} from '../../../../../modules/shared/components/inputs/input-text-autocomplete/input-text-autocomplete.component';
import { AutoCompleteCompleteEvent } from '@openng/optimus-ui/autocomplete';
import { IplpRecipeDetailObject } from '@libs/types/src/list/object-type/iplp-recipe.object';
import {
    FolderEPrescriptionMagistraliterRecipesComponent,
} from '../folder-e-prescription-magistraliter-recipes/folder-e-prescription-magistraliter-recipes.component';
import { getEPrescriptionValidTo } from '@libs/types/src/services/utils/e-prescription-validity.utils';
import {
    E_PRESCRIPTION_DIAGNOSIS_MAX_LENGTH, isEPrescriptionDiagnosisRequired, normalizeEPrescriptionDiagnosis,
} from '@libs/types/src/services/utils/e-prescription-diagnosis.utils';
import {
    DiagnosisSelectInputComponent,
} from '../../../diagnosis-select-input/diagnosis-select-input.component';

type MagistraliterIngredientForm = FormGroup<{
    type: FormControl<EPrescriptionMagistraliterIngredientTypeEnum | null>;
    name: FormControl<string | null>;
    amount: FormControl<number | null>;
    unit: FormControl<EPrescriptionIngredientUnitEnum | null>;
    substanceCode: FormControl<string | null>;
    registeredMedicationCode: FormControl<string | null>;
}>;

@Component({
    selector: 'app-folder-e-prescription-form',
    templateUrl: 'folder-e-prescription-form.component.html',
    styleUrls: ['folder-e-prescription-form.component.scss'],
    providers: [
        FolderEPrescriptionFormStore,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SaveButtonComponent,
        TranslatePipe,
        SelectInputComponent,
        InsuranceCompanySelectInputComponent,
        SelectMedicationComponent,
        RadioInputComponent,
        ServiceDoctorSelectInputComponent,
        NumberInputComponent,
        TextInputComponent,
        CheckInputComponent,
        AddressInputComponent,
        PhoneInputComponent,
        ButtonComponent,
        TextAreaInputComponent,
        WarnFloatMessageComponent,
        InputTextAutocompleteComponent,
        FolderEPrescriptionMagistraliterRecipesComponent,
        DiagnosisSelectInputComponent,
    ],
})
export class FolderEPrescriptionFormComponent implements OnInit {
    public readonly store = inject(FolderEPrescriptionFormStore);
    public readonly dialogService = inject(BaseDialogService);
    private readonly prescriptionMedicationService = inject(FolderEPrescriptionMedicationService);

    // Are set only when component is wrapped in modal
    public folderIdInput = input<number>(0);
    public closed = output<boolean>();

    public prescription = input<EPrescriptionObject | null>();
    public preselectMedication = input<EPrescriptionMedicationRecordObject | null>(null);

    protected prefix = 'ePrescriptions.';
    protected readonly magistraliterEnabled = featureOn.E_PRESCRIPTION_MAGISTRALITER;
    protected form = new AppFormGroup({
        doctorId: fb.control<string | null>(null, Validators.required),
        insuranceAbbr: fb.control<string | null>(null, Validators.required),

        fullName: fb.control<string | null>({ value: null, disabled: true }),
        insuranceNumber: fb.control<string | null>({
            value: null,
            disabled: true,
        }),
        address: fb.control<AddressDetailObject | null>(
            null,
            Validators.required,
        ),
        itemType: fb.control<EPrescriptionItemTypeEnum>(
            EPrescriptionItemTypeEnum.MEDICATION,
            Validators.required,
        ),

        medicationCode1: fb.control<string | null>(null, Validators.required),
        packages1: fb.control<number | null>(null, Validators.required),
        checkP1: fb.control<boolean>(false),
        denturSignentur1: fb.control<string | null>(null, Validators.required),
        addPaidByEmployer1: fb.control<boolean>(false),

        medicationCode2: fb.control<string | null>(null),
        packages2: fb.control<number | null>(null),
        checkP2: fb.control<boolean>(false),
        denturSignentur2: fb.control<string | null>(null),
        addPaidByEmployer2: fb.control<boolean>(false),

        magistraliterName: fb.control<string | null>(null, [Validators.required, Validators.maxLength(146)]),
        magistraliterPreparationProcedure: fb.control<string | null>(null, [
            Validators.required,
            Validators.maxLength(4000),
        ]),
        magistraliterAdministrationRoute: fb.control<string | null>(null, Validators.maxLength(15)),
        magistraliterForm: fb.control<string | null>(null, Validators.maxLength(27)),
        magistraliterAmount: fb.control<number | null>(1, [
            Validators.required,
            Validators.min(1),
            Validators.max(999),
        ]),
        magistraliterDosing: fb.control<string | null>(null, [Validators.required, Validators.maxLength(80)]),
        magistraliterPCheck: fb.control<boolean>(false),
        magistraliterPaymentByEmployer: fb.control<boolean>(false),
        magistraliterIngredients: fb.array<MagistraliterIngredientForm>([]),

        notificationType: fb.control<EPrescriptionNotificationEnum>(
            EPrescriptionNotificationEnum.SMS,
            Validators.required,
        ),
        notificationContact: fb.control<string | null>(null, Validators.required),

        note: fb.control<string | null>(null),
        diagnosis: fb.control<string | null>(null),
    });

    protected folder = computed(() => this.store.folder() || this.store.data.getFolder());
    protected notificationTypeChanged = toSignal(
        this.form.controls.notificationType.valueChanges,
        { initialValue: this.form.controls.notificationType.value },
    );
    protected itemTypeChanged = toSignal(
        this.form.controls.itemType.valueChanges,
        { initialValue: this.form.controls.itemType.value },
    );
    protected isCannabisMagistraliter = computed(() => {
        this.magistraliterIngredientsVersion();
        return this.form.controls.magistraliterIngredients.controls.some(ingredientForm => (
            ingredientForm.controls.type.value === EPrescriptionMagistraliterIngredientTypeEnum.CANNABIS
        ));
    });

    private medicationCode2 = toSignal(
        this.form.controls.medicationCode2.valueChanges,
    );
    private readonly folderId = computed(() => this.folderIdInput() || this.folder()?.id);
    private readonly notificationEmailValidator: ValidatorFn = (control) =>
        Validators.email(control) ? FieldErrorFactory.emailError() : null;
    private readonly magistraliterIngredientsVersion = signal(0);
    private readonly medicationControlNames = [
        'medicationCode1',
        'packages1',
        'checkP1',
        'denturSignentur1',
        'addPaidByEmployer1',
        'medicationCode2',
        'packages2',
        'checkP2',
        'denturSignentur2',
        'addPaidByEmployer2',
    ] as const;
    private readonly magistraliterControlNames = [
        'magistraliterName',
        'magistraliterPreparationProcedure',
        'magistraliterAdministrationRoute',
        'magistraliterForm',
        'magistraliterAmount',
        'magistraliterDosing',
        'magistraliterPCheck',
        'magistraliterPaymentByEmployer',
    ] as const;

    constructor() {
        effect(() => {
            const validators = [Validators.maxLength(E_PRESCRIPTION_DIAGNOSIS_MAX_LENGTH)];
            if (isEPrescriptionDiagnosisRequired(this.itemTypeChanged(), this.isCannabisMagistraliter())) {
                validators.push(Validators.required);
            }
            this.form.controls.diagnosis.setValidators(validators);
            this.form.controls.diagnosis.updateValueAndValidity();
        });

        effect(
            () => {
                const userId =
                    this.store.globalGlobalStore.loggedUserStore.currentUserId();
                const folder = this.folder();

                // TODO zobecnit
                const currentAddress =
                    (folder?.person?.addresses || []).find((it) => {
                        const actDate = new Date();
                        const from = it.validFrom
                            ? new Date(it.validFrom)
                            : null;
                        const to = it.validTo ? new Date(it.validTo) : null;

                        return (
                            (!from || from < actDate) && (!to || to > actDate)
                        );
                    }) || null;

                const currentInsurance = getCurrentInsurance(folder);

                this.form.patchValue({
                    doctorId: userId,
                    fullName: formatPerson(folder?.person),
                    insuranceNumber: folder?.person?.insuranceNumber,
                    address: currentAddress?.address as any,
                    insuranceAbbr: currentInsurance?.insuranceAbbr,
                });
            },
        );

        effect(() => {
            const notificationType = this.notificationTypeChanged();
            const person = this.folder()?.person;
            const notificationContact = this.form.controls.notificationContact;

            notificationContact.setValidators(
                notificationType === EPrescriptionNotificationEnum.EMAIL
                    ? [Validators.required, this.notificationEmailValidator]
                    : [Validators.required],
            );
            notificationContact.setValue(
                notificationType === EPrescriptionNotificationEnum.EMAIL
                    ? person?.email ?? null
                    : person?.mobile ?? person?.phone ?? null,
            );
        });

        effect(() => {
            const isMagistraliter = this.itemTypeChanged() === EPrescriptionItemTypeEnum.MAGISTRALITER;
            this.setControlsEnabled(this.medicationControlNames, !isMagistraliter);
            this.setControlsEnabled(this.magistraliterControlNames, isMagistraliter);
            if (isMagistraliter) {
                this.form.controls.magistraliterIngredients.enable({ emitEvent: false });
                this.form.controls.magistraliterIngredients.controls.forEach(ingredientForm => (
                    this.configureMagistraliterIngredientForm(ingredientForm, false)
                ));
            } else {
                this.form.controls.magistraliterIngredients.disable({ emitEvent: false });
            }
        });

        effect(
            () => {
                const prescription = this.prescription();
                if (!prescription) return;
                this.form.patchValue({
                    diagnosis: prescription.diagnosis ?? null,
                    itemType: this.itemTypeFromValue(prescription.itemType),
                    medicationCode1: prescription?.medications?.[0]?.code,
                    packages1: prescription?.medications?.[0]?.amount,
                    checkP1: prescription?.medications?.[0]?.pCheck,
                    denturSignentur1: prescription?.medications?.[0]?.dosing,
                    addPaidByEmployer1: prescription?.medications?.[0]?.paymentByEmployer,

                    medicationCode2: prescription?.medications?.[1]?.code,
                    packages2: prescription?.medications?.[1]?.amount,
                    checkP2: prescription?.medications?.[1]?.pCheck,
                    denturSignentur2: prescription?.medications?.[1]?.dosing,
                    addPaidByEmployer2: prescription?.medications?.[1]?.paymentByEmployer,

                    magistraliterName: prescription.magistraliter?.name,
                    magistraliterPreparationProcedure: prescription.magistraliter?.preparationProcedure,
                    magistraliterAdministrationRoute: prescription.magistraliter?.administrationRoute,
                    magistraliterForm: prescription.magistraliter?.form,
                    magistraliterAmount: prescription.magistraliter?.amount,
                    magistraliterDosing: prescription.magistraliter?.dosing,
                    magistraliterPCheck: prescription.magistraliter?.pCheck,
                    magistraliterPaymentByEmployer: prescription.magistraliter?.paymentByEmployer,
                }, { isCodeUpdate: true });
                this.setMagistraliterIngredients(prescription.magistraliter?.ingredients || []);
            },
        );

        effect(() => {
            const medicationCode2 = this.medicationCode2();
            if (this.itemTypeChanged() === EPrescriptionItemTypeEnum.MEDICATION && medicationCode2) {
                this.form.controls.packages2.setValidators([
                    Validators.required,
                ]);
                this.form.controls.packages2.updateValueAndValidity();
                this.form.controls.denturSignentur2.setValidators([
                    Validators.required,
                ]);
                this.form.controls.denturSignentur2.updateValueAndValidity();
            } else {
                this.form.controls.packages2.clearValidators();
                this.form.controls.packages2.updateValueAndValidity();
                this.form.controls.denturSignentur2.clearValidators();
                this.form.controls.denturSignentur2.updateValueAndValidity();
            }
        });

        effect(() => {
            const record = this.preselectMedication();
            if (record) untracked(() => this.applyMedicationRecord(record));
        });

        effect(() => {
            const folderId = this.folderId();
            if (folderId) untracked(() => this.store.loadAnamnesis(folderId));
        });

        effect(() => {
            const anamnesis = this.store.anamnesis();
            if (this.prescription() || anamnesis?.folderId !== this.folderId()) return;
            const diagnosis = anamnesis?.diagnosis?.code;
            const control = this.form.controls.diagnosis;
            if (diagnosis && !control.value && !control.dirty) {
                this.form.patchValue({ diagnosis }, { isCodeUpdate: true });
            }
        });
    }

    ngOnInit() {
        if (this.folderIdInput()) {
            const frag = `
                ${folderFrag}
                serviceId
            `;
            this.store.api.getFolder({
                id: this.folderIdInput(),
            }, frag).then();
        }
    }

    protected sendBySMSEmail(event: Event): void {
        this.dialogService.open(FolderEPrescriptionNotificationModalComponent, {
            header: translate.instant('ePrescriptions.notificationSend.title'),
            styleClass: 'p-dialog-width-30',
        });
    }

    protected async saveAndSend(): Promise<void> {
        if (this.form.isInvalid()) return;
        const value = this.form.getRawValue();
        const itemType = this.itemTypeFromValue(value.itemType);
        const magistraliter = itemType === EPrescriptionItemTypeEnum.MAGISTRALITER
            ? this.createMagistraliterInput() : null;
        const containsCannabis = magistraliter?.ingredients?.some(ingredient => (
            isEPrescriptionCannabisSubstanceCode(ingredient.substanceCode)
        )) ?? false;
        const issuedAt = new Date();
        const validTo = getEPrescriptionValidTo(issuedAt, containsCannabis);
        const medications = [
            value.medicationCode1
                ? <EPrescriptionMedicationUpsertInput>{
                    code: value.medicationCode1,
                    amount: Number(value.packages1 || 0),
                    dosing: value.denturSignentur1 || '',
                    paymentByEmployer: value.addPaidByEmployer1 || false,
                    pCheck: value.checkP1 || false,
                }
                : null,
            value.medicationCode2
                ? <EPrescriptionMedicationUpsertInput>{
                    code: value.medicationCode2,
                    amount: Number(value.packages2 || 0),
                    dosing: value.denturSignentur2 || '',
                    paymentByEmployer: value.addPaidByEmployer2 || false,
                    pCheck: value.checkP2 || false,
                }
                : null,
        ].filter((medication): medication is EPrescriptionMedicationUpsertInput => medication !== null);

        const res = await this.store.api.createEPrescription({
            folderId: this.folder()!.id,
            doctorId: value.doctorId || '',
            address: mapAddress(value.address as AddressDetailObject),
            insuranceAbbr: value.insuranceAbbr || '',
            acute: false,
            issuedAt: issuedAt.toISOString(),
            validTo: validTo.toISOString(),
            note: value.note || '',
            notificationType: value.notificationType!,
            notificationContact: value.notificationContact || '',
            itemType,
            medications: itemType === EPrescriptionItemTypeEnum.MEDICATION ? medications : [],
            magistraliter,
            diagnosis: normalizeEPrescriptionDiagnosis(value.diagnosis),
        });

        if (res) {
            contentGlobalSuccess.next(this.prefix + 'message.created');
            if (res.itemType === EPrescriptionItemTypeEnum.MEDICATION && this.shouldSaveToMedication()) {
                await this.prescriptionMedicationService.upsertMedications(
                    res, this.folder(), this.store.globalGlobalStore.currentService(),
                );
            }
            this.closed.emit(true);
        }
    }

    protected addMagistraliterIngredient(): void {
        this.form.controls.magistraliterIngredients.push(this.createMagistraliterIngredientForm());
        this.refreshMagistraliterIngredientState();
    }

    protected removeMagistraliterIngredient(index: number): void {
        this.form.controls.magistraliterIngredients.removeAt(index);
        this.refreshMagistraliterIngredientState();
    }

    protected openMagistraliterTemplates(): void {
        const ref = this.dialogService.open(FolderEPrescriptionMagistraliterTemplatesDialogComponent, {
            header: translate.instant('ePrescriptions.magistraliterTemplates.title'),
            styleClass: 'p-dialog-width-60',
        });
        ref.onClose.subscribe((template: EPrescriptionMagistraliterTemplateObject | undefined) => {
            if (template) this.applyMagistraliterTemplate(template);
        });
    }

    protected async saveMagistraliterTemplate(): Promise<void> {
        this.magistraliterControlNames.forEach(name => this.form.controls[name].markAsTouched());
        this.form.controls.magistraliterIngredients.markAllAsTouched();
        const invalid = this.magistraliterControlNames.some(name => this.form.controls[name].invalid)
            || this.form.controls.magistraliterIngredients.invalid;
        if (invalid) return;
        await this.store.api.createEPrescriptionMagistraliterTemplate(this.createMagistraliterInput());
        contentGlobalSuccess.next(this.prefix + 'message.templateSaved');
    }

    protected applyMagistraliterRecipe(recipe: IplpRecipeDetailObject): void {
        this.form.patchValue({
            magistraliterName: recipe.name,
            magistraliterPreparationProcedure: recipe.prescription,
            magistraliterAdministrationRoute: null,
            magistraliterForm: null,
            magistraliterAmount: 1,
            magistraliterDosing: recipe.dosing || null,
        }, { isCodeUpdate: true });
        this.setMagistraliterIngredients([]);
        this.magistraliterControlNames.forEach(name => this.form.controls[name].markAsTouched());
        this.form.markAsDirty();
    }

    protected magistraliterIngredientTypeChanged(ingredientForm: MagistraliterIngredientForm): void {
        this.configureMagistraliterIngredientForm(ingredientForm, true);
        this.refreshMagistraliterIngredientState();
    }

    protected cannabisSubstanceSelected(ingredientForm: MagistraliterIngredientForm, value: unknown): void {
        if (!value || typeof value !== 'object' || !('value' in value) || !('ingredientName' in value)) {
            ingredientForm.controls.name.setValue(null);
            this.refreshMagistraliterIngredientState();
            return;
        }
        const substance = value as { value: string; ingredientName: string };
        ingredientForm.patchValue({ name: substance.ingredientName, substanceCode: substance.value });
        this.refreshMagistraliterIngredientState();
    }

    protected registeredMedicationSelected(
        ingredientForm: MagistraliterIngredientForm,
        medication: MedicationGridItemObject | null,
    ): void {
        const name = medication ? [medication.name, medication.nameAddition].filter(Boolean).join(' ') : null;
        ingredientForm.controls.name.setValue(name?.slice(0, 200) || null);
        const unit = getEPrescriptionIngredientUnitForForm(medication?.formCode);
        if (unit) ingredientForm.controls.unit.setValue(unit);
    }

    protected readonly suggestMagistraliterIngredientNames = async (
        event: AutoCompleteCompleteEvent,
    ): Promise<void> => {
        const search = event.query.trim();
        const serviceId = this.folder()?.serviceId
            ?? this.store.globalGlobalStore.currentService()?.id
            ?? 0;
        if (search.length < 2 || !serviceId) {
            this.store.data.searchEPrescriptionMagistraliterIngredientNames.set([]);
            return;
        }
        await this.store.api.searchEPrescriptionMagistraliterIngredientNames({ search, serviceId, limit: 20 });
    };

    private createMagistraliterIngredientForm(
        ingredient?: EPrescriptionMagistraliterIngredientUpsertInput,
    ): MagistraliterIngredientForm {
        const type = this.magistraliterIngredientTypeFromValue(ingredient);
        const ingredientForm = new AppFormGroup({
            type: fb.control<EPrescriptionMagistraliterIngredientTypeEnum>(type, Validators.required),
            name: fb.control<string | null>(ingredient?.name || null, [
                Validators.required,
                Validators.maxLength(200),
            ]),
            amount: fb.control<number | null>(ingredient?.amount ?? null, [
                Validators.required,
                Validators.min(0.000001),
                Validators.max(999999999.999999),
            ]),
            unit: fb.control<EPrescriptionIngredientUnitEnum | null>(
                this.ingredientUnitFromValue(ingredient?.unit),
                Validators.required,
            ),
            substanceCode: fb.control<string | null>(ingredient?.substanceCode || null),
            registeredMedicationCode: fb.control<string | null>(ingredient?.registeredMedicationCode || null),
        });
        this.configureMagistraliterIngredientForm(ingredientForm, false);
        return ingredientForm;
    }

    private createMagistraliterInput(): EPrescriptionMagistraliterUpsertInput {
        const value = this.form.getRawValue();

        return {
            name: value.magistraliterName || '',
            preparationProcedure: value.magistraliterPreparationProcedure || '',
            administrationRoute: value.magistraliterAdministrationRoute || null,
            form: value.magistraliterForm || null,
            amount: Number(value.magistraliterAmount || 0),
            dosing: value.magistraliterDosing || '',
            paymentByEmployer: value.magistraliterPaymentByEmployer || false,
            pCheck: value.magistraliterPCheck || false,
            ingredients: this.form.controls.magistraliterIngredients.controls.map(ingredientForm => {
                const ingredient = ingredientForm.getRawValue();
                const isCannabis = ingredient.type === EPrescriptionMagistraliterIngredientTypeEnum.CANNABIS;
                const isRegisteredMedication = ingredient.type
                    === EPrescriptionMagistraliterIngredientTypeEnum.REGISTERED_MEDICATION;
                return {
                    name: ingredient.name || '',
                    amount: Number(ingredient.amount || 0),
                    unit: isCannabis ? EPrescriptionIngredientUnitEnum.GRAM
                        : ingredient.unit || EPrescriptionIngredientUnitEnum.GRAM,
                    substanceCode: isRegisteredMedication ? null : ingredient.substanceCode || null,
                    registeredMedicationCode: isRegisteredMedication
                        ? ingredient.registeredMedicationCode || null
                        : null,
                };
            }),
        };
    }

    private setMagistraliterIngredients(
        ingredients: EPrescriptionMagistraliterIngredientUpsertInput[],
    ): void {
        const ingredientsForm = this.form.controls.magistraliterIngredients;
        ingredientsForm.clear();
        ingredients.forEach(ingredient => {
            ingredientsForm.push(this.createMagistraliterIngredientForm(ingredient));
        });
        if (this.form.controls.itemType.value === EPrescriptionItemTypeEnum.MAGISTRALITER) {
            ingredientsForm.enable({ emitEvent: false });
            ingredientsForm.controls.forEach(ingredientForm => (
                this.configureMagistraliterIngredientForm(ingredientForm, false)
            ));
        } else {
            ingredientsForm.disable({ emitEvent: false });
        }
        this.refreshMagistraliterIngredientState();
    }

    private applyMedicationRecord(record: EPrescriptionMedicationRecordObject): void {
        if (record.magistraliter) {
            if (!this.magistraliterEnabled) return;
            this.form.patchValue({
                itemType: EPrescriptionItemTypeEnum.MAGISTRALITER,
                magistraliterName: record.name,
                magistraliterPreparationProcedure: record.magistraliter.preparationProcedure,
                magistraliterAdministrationRoute: record.magistraliter.administrationRoute ?? null,
                magistraliterForm: record.magistraliter.form ?? null,
                magistraliterAmount: record.amount,
                magistraliterDosing: record.dosing,
            }, { isCodeUpdate: true });
            this.setMagistraliterIngredients(record.magistraliter.ingredients ?? []);
            return;
        }
        this.form.patchValue({
            itemType: EPrescriptionItemTypeEnum.MEDICATION,
            medicationCode1: record.code,
            packages1: record.amount,
            denturSignentur1: record.dosing,
        });
    }

    private applyMagistraliterTemplate(template: EPrescriptionMagistraliterTemplateObject): void {
        this.form.patchValue({
            magistraliterName: template.name,
            magistraliterPreparationProcedure: template.preparationProcedure,
            magistraliterAdministrationRoute: template.administrationRoute,
            magistraliterForm: template.form,
            magistraliterAmount: template.amount,
            magistraliterDosing: template.dosing,
            magistraliterPCheck: template.pCheck,
            magistraliterPaymentByEmployer: template.paymentByEmployer,
        }, { isCodeUpdate: true });
        this.setMagistraliterIngredients(template.ingredients);
    }

    private configureMagistraliterIngredientForm(
        ingredientForm: MagistraliterIngredientForm,
        clearValues: boolean,
    ): void {
        const type = ingredientForm.controls.type.value;
        const substanceCode = ingredientForm.controls.substanceCode;
        const registeredMedicationCode = ingredientForm.controls.registeredMedicationCode;
        substanceCode.clearValidators();
        registeredMedicationCode.clearValidators();
        if (clearValues) {
            ingredientForm.patchValue({ name: null, substanceCode: null, registeredMedicationCode: null });
        }
        ingredientForm.controls.unit.enable({ emitEvent: false });
        if (type === EPrescriptionMagistraliterIngredientTypeEnum.CANNABIS) {
            substanceCode.setValidators(Validators.required);
            ingredientForm.controls.unit.setValue(EPrescriptionIngredientUnitEnum.GRAM, { emitEvent: false });
            ingredientForm.controls.unit.disable({ emitEvent: false });
        } else if (type === EPrescriptionMagistraliterIngredientTypeEnum.REGISTERED_MEDICATION) {
            registeredMedicationCode.setValidators(Validators.required);
        }
        substanceCode.updateValueAndValidity({ emitEvent: false });
        registeredMedicationCode.updateValueAndValidity({ emitEvent: false });
    }

    private refreshMagistraliterIngredientState(): void {
        this.magistraliterIngredientsVersion.update(version => version + 1);
        const administrationRoute = this.form.controls.magistraliterAdministrationRoute;
        administrationRoute.setValidators(this.isCannabisMagistraliter()
            ? [Validators.required, Validators.maxLength(15)]
            : Validators.maxLength(15));
        administrationRoute.updateValueAndValidity({ emitEvent: false });
    }

    private setControlsEnabled(
        controlNames: readonly (keyof typeof this.form.controls)[],
        enabled: boolean,
    ): void {
        controlNames.forEach(controlName => {
            const control = this.form.controls[controlName];
            if (enabled) {
                control.enable({ emitEvent: false });
            } else {
                control.disable({ emitEvent: false });
            }
        });
    }

    private itemTypeFromValue(itemType?: string | null): EPrescriptionItemTypeEnum {
        return this.magistraliterEnabled && itemType === EPrescriptionItemTypeEnum.MAGISTRALITER
            ? EPrescriptionItemTypeEnum.MAGISTRALITER
            : EPrescriptionItemTypeEnum.MEDICATION;
    }

    private ingredientUnitFromValue(unit?: string | null): EPrescriptionIngredientUnitEnum {
        return unit === EPrescriptionIngredientUnitEnum.PIECE
            ? EPrescriptionIngredientUnitEnum.PIECE
            : EPrescriptionIngredientUnitEnum.GRAM;
    }

    private magistraliterIngredientTypeFromValue(
        ingredient?: EPrescriptionMagistraliterIngredientUpsertInput,
    ): EPrescriptionMagistraliterIngredientTypeEnum {
        if (isEPrescriptionCannabisSubstanceCode(ingredient?.substanceCode)) {
            return EPrescriptionMagistraliterIngredientTypeEnum.CANNABIS;
        }
        return ingredient?.registeredMedicationCode
            ? EPrescriptionMagistraliterIngredientTypeEnum.REGISTERED_MEDICATION
            : EPrescriptionMagistraliterIngredientTypeEnum.RAW_MATERIAL;
    }

    private shouldSaveToMedication(): boolean {
        const serviceId = this.folder()?.serviceId
            ?? this.store.globalGlobalStore.currentService()?.id
            ?? 0;
        const settings = this.store.globalGlobalStore.organizationStore
            .getServiceSetting<ServiceSettingsEprescriptionType>(
                serviceId,
                SERVICE_SETTINGS_EPRESCRIPTIONS_TYPE_KEY,
                SERVICE_SETTINGS_EPRESCRIPTIONS_TYPE_DEFAULT,
            );

        return settings.saveToMedicationOnSend ?? false;
    }

    protected readonly ePrescriptionNotificationEnumOptions = ePrescriptionNotificationEnumOptions;
    protected readonly EPrescriptionNotificationEnum = EPrescriptionNotificationEnum;
    protected readonly EPrescriptionItemTypeEnum = EPrescriptionItemTypeEnum;
    protected readonly EPrescriptionMagistraliterIngredientTypeEnum = EPrescriptionMagistraliterIngredientTypeEnum;
    protected readonly ePrescriptionItemTypeEnumOptions = ePrescriptionItemTypeEnumOptions;
    protected readonly ePrescriptionIngredientUnitEnumOptions = ePrescriptionIngredientUnitEnumOptions;
    protected readonly ePrescriptionMagistraliterIngredientTypeEnumOptions =
        ePrescriptionMagistraliterIngredientTypeEnumOptions;
    protected readonly cannabisSubstances = E_PRESCRIPTION_CANNABIS_SUBSTANCES;
}
